import type { MBTIType } from '../types';
import type { Archetype } from './archetypes';

export const sexAndTheCityCharacters = [
  {
    id: 'carrie',
    name: 'Carrie Bradshaw',
    tagline: 'The curious narrator',
    description:
      'A hundred questions, a little spontaneity, and a story in every connection. You tend to think out loud and look for meaning in the everyday.',
    line: 'And just like that… you have another question.',
    traits: {
      openness: 90,
      conscientiousness: 35,
      extraversion: 70,
      agreeableness: 65,
      neuroticism: 75,
    },
  },
  {
    id: 'miranda',
    name: 'Miranda Hobbes',
    tagline: 'The wonderfully straight shooter',
    description:
      'Practical, independent, and a fan of getting to the point. You show up for people with a clear head and a plan that actually works.',
    line: 'The group chat has a plan. You made it.',
    traits: {
      openness: 55,
      conscientiousness: 90,
      extraversion: 45,
      agreeableness: 40,
      neuroticism: 55,
    },
  },
  {
    id: 'charlotte',
    name: 'Charlotte York',
    tagline: 'The devoted optimist',
    description:
      'Thoughtful, steady, and invested in your people. You make room for warmth and care, and you like a little intention behind the big moments.',
    line: 'You brought the flowers. And remembered the date.',
    traits: {
      openness: 45,
      conscientiousness: 85,
      extraversion: 55,
      agreeableness: 90,
      neuroticism: 50,
    },
  },
  {
    id: 'samantha',
    name: 'Samantha Jones',
    tagline: 'The unapologetic adventurer',
    description:
      'Bold, sociable, and comfortable taking the lead. You bring energy to a room and tend to say what you mean without a three-paragraph preamble.',
    line: 'Your comfort zone has a very generous radius.',
    traits: {
      openness: 80,
      conscientiousness: 65,
      extraversion: 95,
      agreeableness: 40,
      neuroticism: 20,
    },
  },
] as const satisfies readonly Archetype[];

export const hogwartsHouses = [
  {
    id: 'gryffindor',
    name: 'Gryffindor',
    tagline: 'The first one in',
    description:
      'Bold, quick to commit, and happiest when something is happening. You would rather try and laugh about it than wait and wonder.',
    line: 'You replied ‘I’m in’ before they finished typing.',
    traits: {
      openness: 65,
      conscientiousness: 45,
      extraversion: 85,
      agreeableness: 55,
      neuroticism: 30,
    },
  },
  {
    id: 'hufflepuff',
    name: 'Hufflepuff',
    tagline: 'The loyal one',
    description:
      'Warm, steady, and quietly holding the group together. People tell you things because you keep them.',
    line: 'You’re the one who asks how the interview went.',
    traits: {
      openness: 50,
      conscientiousness: 75,
      extraversion: 55,
      agreeableness: 90,
      neuroticism: 45,
    },
  },
  {
    id: 'ravenclaw',
    name: 'Ravenclaw',
    tagline: 'The curious mind',
    description:
      'Thoughtful, inventive, and never short on a better question. Your messages come with footnotes.',
    line: 'You sent a link with “this explains everything.”',
    traits: {
      openness: 90,
      conscientiousness: 60,
      extraversion: 40,
      agreeableness: 55,
      neuroticism: 50,
    },
  },
  {
    id: 'slytherin',
    name: 'Slytherin',
    tagline: 'The one with a plan',
    description:
      'Ambitious, strategic, and fiercely loyal to your inner circle. You know exactly what you want.',
    line: 'You already know how this ends. You planned it.',
    traits: {
      openness: 55,
      conscientiousness: 85,
      extraversion: 60,
      agreeableness: 35,
      neuroticism: 40,
    },
  },
] as const satisfies readonly Archetype[];

export const friendsCharacters = [
  {
    id: 'monica',
    name: 'Monica',
    tagline: 'The one who hosts',
    description:
      'Organized, competitive, and devoted to your people. Everything is better with a plan and a clean kitchen.',
    line: 'You made a spreadsheet for the group trip.',
    traits: {
      openness: 50,
      conscientiousness: 95,
      extraversion: 75,
      agreeableness: 60,
      neuroticism: 65,
    },
  },
  {
    id: 'chandler',
    name: 'Chandler',
    tagline: 'The one with the joke',
    description:
      'Quick, self-aware, and funniest when it matters. You deflect with a joke and mean every word of the follow-up.',
    line: 'Could you BE any quicker with a comeback?',
    traits: {
      openness: 60,
      conscientiousness: 45,
      extraversion: 55,
      agreeableness: 60,
      neuroticism: 75,
    },
  },
  {
    id: 'joey',
    name: 'Joey',
    tagline: 'The loyal friend',
    description:
      'Easygoing, generous, and all in on the people you love. Life is simple: friends, food, and showing up.',
    line: 'You never ask ‘how you doin’’ without meaning it.',
    traits: {
      openness: 35,
      conscientiousness: 30,
      extraversion: 90,
      agreeableness: 85,
      neuroticism: 35,
    },
  },
  {
    id: 'phoebe',
    name: 'Phoebe',
    tagline: 'The free spirit',
    description:
      'Unconventional, kind, and unbothered by what anyone thinks. You see the world a little sideways, and it suits you.',
    line: 'You sent a song instead of an answer.',
    traits: {
      openness: 95,
      conscientiousness: 30,
      extraversion: 65,
      agreeableness: 80,
      neuroticism: 40,
    },
  },
  {
    id: 'rachel',
    name: 'Rachel',
    tagline: 'The one everyone texts',
    description:
      'Warm, social, and always in the middle of the story. You keep growing, and it shows.',
    line: 'You changed the plan and everyone was happier.',
    traits: {
      openness: 60,
      conscientiousness: 45,
      extraversion: 85,
      agreeableness: 65,
      neuroticism: 55,
    },
  },
  {
    id: 'ross',
    name: 'Ross',
    tagline: 'The one who knows things',
    description:
      'Earnest, curious, and full of facts you are excited to share. You care deeply, sometimes loudly.',
    line: 'You corrected a fact and followed up with a source.',
    traits: {
      openness: 85,
      conscientiousness: 70,
      extraversion: 50,
      agreeableness: 55,
      neuroticism: 70,
    },
  },
] as const satisfies readonly Archetype[];

/** SoulThread's own names for the sixteen types, with the line each one opens with. */
export const myersBriggsTypes = {
  INTJ: {
    name: 'The Strategist',
    description:
      'Independent and far-sighted. You see where things are heading and quietly get there first.',
    line: 'You had a five-year plan before the group chat picked a restaurant.',
  },
  INTP: {
    name: 'The Theorist',
    description:
      'Curious and precise. You would rather understand something than simply agree with it.',
    line: 'You answered a yes-or-no question with “well, technically.”',
  },
  ENTJ: {
    name: 'The Organizer',
    description: 'Decisive and driven. You turn loose ideas into plans and plans into done.',
    line: 'You assigned roles in a chat that was just for memes.',
  },
  ENTP: {
    name: 'The Instigator',
    description:
      'Quick, playful, and endlessly inventive. A good debate is your idea of a good time.',
    line: 'You argued the other side just to see what would happen.',
  },
  INFJ: {
    name: 'The Confidant',
    description:
      'Perceptive and principled. You read between the lines and care about what you find there.',
    line: 'You knew that ‘lol’ was sad before they did.',
  },
  INFP: {
    name: 'The Idealist',
    description:
      'Gentle, imaginative, and true to yourself. You feel things fully and choose your words with care.',
    line: 'You drafted three replies and sent none of them.',
  },
  ENFJ: {
    name: 'The Rallier',
    description: 'Warm and encouraging. You bring out the best in people and bring them together.',
    line: 'You checked in on everyone before anyone asked.',
  },
  ENFP: {
    name: 'The Spark',
    description:
      'Enthusiastic and full of possibility. Your excitement is contagious, and so are your ideas.',
    line: 'Seventeen new ideas, nine exclamation points, zero regrets.',
  },
  ISTJ: {
    name: 'The Anchor',
    description:
      'Reliable and thorough. People trust you because you do exactly what you said you would.',
    line: 'You confirmed the time, the place, and the backup place.',
  },
  ISFJ: {
    name: 'The Keeper',
    description:
      'Caring and attentive. You remember the details that make people feel looked after.',
    line: 'You remembered their dog’s birthday.',
  },
  ESTJ: {
    name: 'The Planner',
    description: 'Practical and direct. You like things clear, fair, and actually happening.',
    line: 'You turned “we should hang out” into a calendar invite.',
  },
  ESFJ: {
    name: 'The Host',
    description: 'Sociable and generous. You keep the group warm, fed, and on speaking terms.',
    line: 'You are the reason the group chat has a birthday reminder.',
  },
  ISTP: {
    name: 'The Fixer',
    description: 'Calm and hands-on. You would rather solve it than talk about solving it.',
    line: 'You replied with a thumbs up and fixed it yourself.',
  },
  ISFP: {
    name: 'The Artist',
    description: 'Easygoing and expressive. You notice beauty and share it without a fuss.',
    line: 'You sent the photo instead of explaining.',
  },
  ESTP: {
    name: 'The Doer',
    description: 'Bold and spontaneous. You learn by jumping in, and you usually land well.',
    line: 'You said “omw” before you had found your keys.',
  },
  ESFP: {
    name: 'The Life of the Party',
    description: 'Fun, warm, and in the moment. Wherever you are is where the plans end up.',
    line: 'You are why everyone’s phone is on 3%.',
  },
} as const satisfies Record<MBTIType, { name: string; description: string; line: string }>;
